import React, { useMemo } from "react";

import { School as GraduationCapIcon } from "@mui/icons-material";
import { Typography, Box, Alert, CircularProgress } from "@mui/material";

import { EmployeePosition } from "../../api/PartnerPortalGatewayApi";
import { groupAndSortEducators } from "../../utils/educatorGrouping";
import { CACard } from "../ui";
import { EducatorSubSection } from "./EducatorSubSection/EducatorSubSection";

interface EducatorsListGridProps {
  data: EmployeePosition[] | undefined;
  isLoading: boolean;
  error: Error | null;
  title?: string;
  subtitle?: string;
}

export const EducatorsListGrid: React.FC<EducatorsListGridProps> = ({
  data,
  isLoading,
  error,
  title = "Educators working this week",
  subtitle = "The following team members will be working at your service this week",
}) => {
  const { leadership, support } = useMemo(() => groupAndSortEducators(data ?? []), [data]);

  if (isLoading) {
    return (
      <CACard>
        <Box
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
          }}
        >
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading educators...
          </Typography>
        </Box>
      </CACard>
    );
  }

  if (error) {
    return (
      <CACard>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      </CACard>
    );
  }

  if (!data || data.length === 0) {
    return (
      <CACard>
        <Box
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
          }}
        >
          <GraduationCapIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
            Before school care (BSC), after school care (ASC) and vacation care (VAC) are not currently running at your
            school due to school holidays.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: "center" }}>
            Educators will appear on this page once BSC, ASC or VAC start up again.
          </Typography>
        </Box>
      </CACard>
    );
  }

  return (
    <CACard>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            <GraduationCapIcon sx={{ fontSize: "1.5rem", color: "secondary.main" }} />
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
              {subtitle}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <EducatorSubSection title="Leadership" educators={leadership} />
          <EducatorSubSection title="Educators & Support" educators={support} />
        </Box>
      </Box>
    </CACard>
  );
};
